import React from "react";

import _ from "lodash";

import useDatePicker, { DatePickerHook } from "commons/hooks/input/useDatePicker";
import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { createLocalDateFromUnixMilli, createUnixMilliFromLocalDate } from "commons/utils/moment";
import { notEmptyValidator } from "commons/utils/validate";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useNewsApi from "hooks/api/private/useNewsApi";
import useNewsImageApi from "hooks/api/private/useNewsImageApi";
import { useNewsId } from "hooks/useId";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import useOrderableImages, { OrderableImagesHook } from "hooks/useOrderableImages";

import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { NewsBody, NewsDetails, NewsTextItem } from "types/api/news";
import { DesktopOrMobile, NewsMediaEnum } from "types/enums";

import { emptyMetaFields, metaFieldsSchema } from "utils/metaFields";
import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type NewsBodyForm = {
    date: number | null;
    enabled: boolean;
    media: NewsMediaEnum;
    url_video: string;
    slug: string;
    cover_img: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: NewsTextItem;
    };
};

export type NewsFormHook = {
    datePickerHookFrom: DatePickerHook;
    formHook: FormHookType<NewsBodyForm>;
    orderableImagesHook: OrderableImagesHook;
    setDesktopMobileImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
};

function useNewsForm(): NewsFormHook {
    const newsId = useNewsId();

    const { createNews, updateNews, getOneNews } = useNewsApi();

    const [schema, setSchema] = React.useState<ObjectPropertyType>({
        type: "object",
        properties: {
            url_video: {
                type: "simple",
                validators: [],
            },
            slug: {
                type: "simple",
                validators: [notEmptyValidator],
            },
            date: {
                type: "simple",
                validators: [notEmptyValidator],
            },
            media: {
                type: "simple",
                validators: [notEmptyValidator],
            },
            cover_img: notEmptyMedia,
            text: {
                type: "collection",
                of: {
                    type: "object",
                    properties: {
                        title: {
                            type: "simple",
                            validators: [notEmptyValidator],
                        },
                        description: {
                            type: "simple",
                            validators: [notEmptyValidator],
                        },
                        ...metaFieldsSchema.of.properties,
                    },
                },
            },
        },
    });

    const initialValues: NewsBodyForm = React.useMemo(() => {
        return {
            date: null,
            enabled: true,
            url_video: "",
            slug: "",
            media: NewsMediaEnum.gallery,
            cover_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    title: "",
                    description: "",
                    ...emptyMetaFields()[LanguageEnum.it],
                },
                en: {
                    title: "",
                    description: "",
                    ...emptyMetaFields()[LanguageEnum.en],
                },
            },
        };
    }, []);

    const formHook = useForm<NewsBodyForm>(schema, initialValues, getExtraErrorsDescription);

    const { checkErrorVisibility, showAllError, setValue, setForm, isValid, form } = formHook;

    React.useEffect(() => {
        if (form.media === NewsMediaEnum.video) {
            setSchema((prevSchema) => {
                const newSchema = _.cloneDeep(prevSchema);

                newSchema.properties.url_video = {
                    type: "simple",
                    validators: [notEmptyValidator],
                };

                return newSchema;
            });
        } else {
            setSchema((prevSchema) => {
                const newSchema = _.cloneDeep(prevSchema);

                newSchema.properties.url_video = {
                    type: "simple",
                    validators: [],
                };

                return newSchema;
            });
        }
    }, [form.media]);

    const { createNewsImage, updateNewsImage, deleteNewsImage } = useNewsImageApi();

    const orderableImagesHook = useOrderableImages({
        createImage: createNewsImage,
        updateImage: updateNewsImage,
        deleteImage: deleteNewsImage,
        fieldId: "news_id",
    });

    const {
        formHook: orderableImagesformHook,
        saveOrderableImages,
        initializeOrderableImage,
    } = orderableImagesHook;

    const { showAllError: showAllErrorOrderableImages, isValid: isValidOrderableImages } =
        orderableImagesformHook;

    const datePickerHookFrom = useDatePicker({
        setValue: setValue,
        checkErrorVisibility: checkErrorVisibility,
        label: "Data",
        value: form.date,
        path: "date",
    });

    const { setDesktopMobileImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "cover_img",
    });

    const [newsDetails, setNewsDetails] = React.useState<NewsDetails | undefined>(undefined);

    React.useEffect(() => {
        if (newsId) {
            const [apiCall, abortController] = getOneNews({ id: newsId });

            apiCall.then(setNewsDetails);

            return () => {
                abortController.abort();
            };
        }
    }, [getOneNews, newsId]);

    // complete form from data
    React.useEffect(() => {
        if (newsDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.date = createUnixMilliFromLocalDate(newsDetails.date);
                newForm.url_video = newsDetails.url_video;
                newForm.slug = newsDetails.slug;
                newForm.enabled = newsDetails.enabled;
                newForm.media = newsDetails.media;

                newForm.text.it.title = newsDetails.text.it.title;
                newForm.text.it.description = newsDetails.text.it.description;
                newForm.text.it.meta_title = newsDetails.text.it.meta_title ?? "";
                newForm.text.it.meta_description = newsDetails.text.it.meta_description ?? "";
                newForm.text.it.meta_keywords = newsDetails.text.it.meta_keywords ?? "";

                newForm.text.en.title = newsDetails.text.en.title;
                newForm.text.en.description = newsDetails.text.en.description;
                newForm.text.en.meta_title = newsDetails.text.en.meta_title ?? "";
                newForm.text.en.meta_description = newsDetails.text.en.meta_description ?? "";
                newForm.text.en.meta_keywords = newsDetails.text.en.meta_keywords ?? "";

                newForm.cover_img.id = newsDetails.cover_img.id;
                newForm.cover_img = {
                    id: newsDetails.cover_img.id,
                    desktop: createAttachmentType({
                        id: newsDetails.cover_img.desktop.id ?? null,
                        label: "Desktop",
                        filename: newsDetails.cover_img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: newsDetails.cover_img.mobile.id ?? null,
                        label: "Mobile",
                        filename: newsDetails.cover_img.mobile.filename,
                        status: 2,
                    }),
                };

                return newForm;
            });
        }
    }, [setForm, newsDetails]);

    // complete orderable images form from data
    React.useEffect(() => {
        if (newsDetails) {
            initializeOrderableImage(newsDetails.images);
        }
    }, [initializeOrderableImage, newsDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction("useNewsForm", () => {
            if (isValid && (form.media === NewsMediaEnum.video || isValidOrderableImages)) {
                const promises: Promise<unknown>[] = [];

                const body: NewsBody = {
                    date: form.date ? createLocalDateFromUnixMilli(form.date) : "",
                    enabled: form.enabled,
                    media: form.media,
                    url_video: form.url_video,
                    slug: form.slug,
                    cover_img: {
                        id: form.cover_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.cover_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.cover_img.mobile),
                    },
                    text: {
                        it: { ...form.text.it },
                        en: { ...form.text.en },
                    },
                };

                if (newsId) {
                    const [apiCall] = updateNews({ id: newsId }, body);

                    promises.push(apiCall);

                    if (form.media === NewsMediaEnum.gallery) {
                        promises.push(saveOrderableImages(newsId));
                    }
                } else {
                    promises.push(
                        new Promise((resolve, reject) => {
                            const [apiCall] = createNews(body);

                            apiCall
                                .then((response) => {
                                    if (form.media === NewsMediaEnum.gallery) {
                                        saveOrderableImages(response.id)
                                            .then(resolve)
                                            .catch(reject);
                                    } else {
                                        resolve(response);
                                    }
                                })
                                .catch(reject);
                        }),
                    );
                }

                return Promise.all(promises);
            } else {
                showAllError();
                showAllErrorOrderableImages();

                return Promise.reject(new Error("INVALID_FORM"));
            }
        });
    }, [
        showAllErrorOrderableImages,
        saveOrderableImages,
        addSaveAction,
        showAllError,
        createNews,
        updateNews,
        isValidOrderableImages,
        isValid,
        newsId,
        form,
    ]);

    return React.useMemo(
        (): NewsFormHook => ({
            setDesktopMobileImage,
            orderableImagesHook,
            datePickerHookFrom,
            formHook,
        }),
        [setDesktopMobileImage, orderableImagesHook, datePickerHookFrom, formHook],
    );
}

export default useNewsForm;
